import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import apiConfig from "../../config/apiConfig";
import {
  AllCommunityModule,
  ModuleRegistry,
  themeAlpine,
  themeBalham,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useQuery } from "@tanstack/react-query";
import { useRaspStore } from "../../store/raspStore";
import { LoginContext } from "../../context/LoginContext";
import RoundsModel from "../../models/RoundsModel";
import { apiFetch } from "../../apis/apiFetch";

ModuleRegistry.registerModules([AllCommunityModule]);

interface ColumnDef {
  field: string;
  headerName: string;
  editable: boolean;
  resizable: boolean;
  sortable: boolean;
  filter: boolean;
  cellRenderer?: (params: any) => React.ReactNode;
}

// Define the custom cell renderer for the action column
const ActionCellRenderer = (props: any) => {
  const handleEdit = () => {
    props.context.handleUpdate(props.data.id);
  };

  return (
    <button onClick={handleEdit} className="btn btn-primary">
      Edit
    </button>
  );
};

export type ResourceMetaData = {
  resource: string;
  fieldValues: any[];
};

const UpdateRounds = () => {
  const { user } = useContext(LoginContext);
  const [rowData, setRowData] = useState<any[]>([]);
  const [colDef1, setColDef1] = useState<any[]>([]);
  const [resMetaData, setResMetaData] = useState<ResourceMetaData[]>([]);
  const [fields, setFields] = useState<any[]>([]);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [fetchData, setFetchedData] = useState<any[]>([]);
  const [editedData, setEditedData] = useState<any>({});
  const [showToast, setShowToast] = useState<any>(false);
  const navigate = useNavigate();
  const apiUrl = `${apiConfig.getResourceUrl("rounds")}?`;
  const metadataUrl = `${apiConfig.getResourceMetaDataUrl("Rounds")}?`;
  const BaseUrl = `${apiConfig.API_BASE_URL}`;
  const regex = /^(g_|archived|extra_data)/;

  const getUserAllData = useRaspStore((s) => s.getUserAllData);
  // getUserIdFromJWT removed - using LoginContext

  const [currentUrl, setCurrentUrl] = useState("");
  // Fetch resource data
  const {
    data: dataRes,
    isLoading: isLoadingDataRes,
    error: errorDataRes,
  } = useQuery({
    queryKey: ["resourceData", "roundsUpdate"],
    queryFn: async () => {
      const params = new URLSearchParams();

      const queryId: any = "GET_ALL";
      params.append("queryId", queryId);
      const response = await apiFetch(
        `${apiConfig.getResourceUrl("rounds")}?` + params.toString(),
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        throw new Error("Error: " + response.status);
      }

      const data = await response.json();
      setFetchedData(data.resource || []);
      const initialEditedData = fetchData.reduce((acc: any, item: any) => {
        acc[item.id] = { ...item };
        return acc;
      }, {});
      return data;
    },
  });

  // ADDED: Fetch drives for lookup (id -> name)
  const { data: drivesData } = useQuery({
    queryKey: ["drivesLookup", "roundsUpdate"],
    queryFn: async () => {
      try {
        const response = await apiFetch(
          `${apiConfig.getResourceUrl("drive")}?queryId=GET_ALL`,
          {
            method: "GET",
          },
        );
        if (!response.ok) return [];
        const data = await response.json();
        return data.resource || []; // array of drive objects
      } catch (e) {
        console.error("Failed to apiFetch drives", e);
        return [];
      }
    },
    enabled: !!dataRes, // only apiFetch if we have rounds data
  });

  // Create lookup map: { "drive_two_uuid": "Drive Two Name", ... }
  const driveLookup = React.useMemo(() => {
    if (!drivesData) return {};
    const map: Record<string, string> = {};
    drivesData.forEach((d: any) => {
      if (d.id && d.name) {
        map[d.id] = d.name;
      }
    });
    return map;
  }, [drivesData]);

  // Fetch metadata
  const {
    data: dataResMeta,
    isLoading: isLoadingDataResMeta,
    error: errorDataResMeta,
  } = useQuery({
    queryKey: ["resourceMetaData", "roundsUpdate"],
    queryFn: async () => {
      const response = await apiFetch(
        `${apiConfig.getResourceMetaDataUrl("rounds")}?`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        throw new Error("Error: " + response.status);
      }

      const data = await response.json();
      setResMetaData(data);
      setFields(data[0]?.fieldValues || []);
      const required = data[0]?.fieldValues
        .filter((field: any) => !regex.test(field.name))
        .map((field: any) => field.name);
      setRequiredFields(required || []);
      return data;
    },
  });

  const handleEdit = (id: any, field: string, value: any) => {
    setEditedData((prevData: any) => ({
      ...prevData,
      [id]: {
        ...(prevData[id] || {}),
        [field]: value,
      },
    }));
  };

  const handleUpdate = async (id: any) => {
    navigate(`/edit/rounds/${id}`);
  };

  // Transform API data into model objects and set row data
  useEffect(() => {
    if (dataRes && dataRes.resource) {
      let dataToStore = { resource: "rounds", records: dataRes.resource };
      useRaspStore.getState().initializeStore(user?.userId || "", dataToStore);
      
      // Map the resource data to our model
      const mappedData = dataRes.resource.map((obj: any) => RoundsModel.fromJson(obj).toJson());
      setRowData(mappedData);
    }
  }, [dataRes, user?.userId]);

  useEffect(() => {
    const fields = ["name", "drive_id", "startdate", "enddate", "cse_range_combined", "ece_range_combined", "ai_range_combined", "withdrawal_end_date"];

    const columns = fields.map((field: any) => {
      let headerName = field.charAt(0).toUpperCase() + field.slice(1);
      if (field === "startdate") headerName = "Start Date";
      if (field === "enddate") headerName = "End Date";
      if (field === "withdrawal_end_date") headerName = "Withdrawal End Date";
      
      const baseCol = {
        field: field,
        headerName,
        editable: false,
        resizable: true,
        sortable: true,
        filter: true,
      };

      if (field === "cse_range_combined") {
        return { 
          ...baseCol, 
          headerName: "CSE Detail", 
          valueGetter: (params: any) => {
            if (params.data?.allocation_type === "CUTOFF_BASED") {
              const min = params.data?.cse_gate_min ?? "0";
              const max = params.data?.cse_gate_max ?? "1000";
              return `[${min} - ${max}]`;
            }
            const count = params.data?.cse_count !== undefined ? params.data.cse_count : (params.data?.cse_seats || "0");
            const cutoff = params.data?.cse_cutoff !== undefined ? params.data.cse_cutoff : "N/A";
            return `${count} candidates (${cutoff} cutoff)`;
          }
        };
      }
      if (field === "ece_range_combined") {
        return { 
          ...baseCol, 
          headerName: "ECE Detail", 
          valueGetter: (params: any) => {
            if (params.data?.allocation_type === "CUTOFF_BASED") {
              const min = params.data?.ece_gate_min ?? "0";
              const max = params.data?.ece_gate_max ?? "1000";
              return `[${min} - ${max}]`;
            }
            const count = params.data?.ece_count !== undefined ? params.data.ece_count : (params.data?.ece_seats || "0");
            const cutoff = params.data?.ece_cutoff !== undefined ? params.data.ece_cutoff : "N/A";
            return `${count} candidates (${cutoff} cutoff)`;
          }
        };
      }
      if (field === "ai_range_combined") {
        return { 
          ...baseCol, 
          headerName: "AI&DS Detail", 
          valueGetter: (params: any) => {
            if (params.data?.allocation_type === "CUTOFF_BASED") {
              const min = params.data?.ai_gate_min ?? "0";
              const max = params.data?.ai_gate_max ?? "1000";
              return `[${min} - ${max}]`;
            }
            const count = params.data?.ai_count !== undefined ? params.data.ai_count : (params.data?.ai_seats || "0");
            const cutoff = params.data?.ai_cutoff !== undefined ? params.data.ai_cutoff : "N/A";
            return `${count} candidates (${cutoff} cutoff)`;
          }
        };
      }
      if (field === "drive_id") {
        return {
          ...baseCol,
          headerName: "Drive Name",
          valueFormatter: (params: any) => {
            const driveId = params.value;
            return driveLookup[driveId] || driveId;
          },
        };
      }

      if (field === "startdate" || field === "enddate" || field === "withdrawal_end_date" || field === "date") {
        return {
          ...baseCol,
          valueFormatter: (params: any) => {
            if (!params.value) return "";
            let d: Date;
            if (typeof params.value === "string") {
              // Check if it's a numeric timestamp in string form
              if (!isNaN(Number(params.value))) {
                d = new Date(Number(params.value));
              } else {
                d = new Date(params.value);
              }
            } else {
              d = new Date(Number(params.value));
            }
            
            if (isNaN(d.getTime())) return params.value;
            
            const day = String(d.getDate()).padStart(2, "0");
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const year = d.getFullYear();
            return `${day}-${month}-${year}`;
          },
        };
      }

      return baseCol;
    });

    // Add the Action column with the custom cell renderer
    columns.push({
      headerName: "Action",
      field: "Action",
      cellRenderer: ActionCellRenderer,
      editable: false,
      resizable: true,
      sortable: false,
      filter: false,
      width: 120,
    } as ColumnDef);
    setColDef1(columns);
  }, [requiredFields, driveLookup]);

  const defaultColDef = {
    flex: 1,
    minWidth: 100,
    editable: false,
    valueFormatter: (params: any) => {
      if (
        params.value === null ||
        params.value === undefined ||
        params.value === ""
      ) {
        return "N/A";
      }
      return params.value;
    },
  };

  return (
    <>
      <div className="mt-4">
        <div>
          {rowData.length === 0 && colDef1.length === 0 ? (
            <div className="text-muted">
              No data available. Please add a resource attribute.
            </div>
          ) : (
            <div
              className="ag-theme-alpine"
              style={{ height: 500, width: "100%" }}
            >
              <AgGridReact
                rowData={rowData}
                columnDefs={colDef1}
                defaultColDef={defaultColDef}
                pagination={true}
                paginationPageSize={10}
                animateRows={true}
                rowSelection="multiple"
                context={{
                  handleUpdate: handleUpdate,
                }}
              />
            </div>
          )}
        </div>
      </div>

      {showToast && (
        <div
          className="toast-container position-fixed top-20 start-50 translate-middle p-3"
          style={{ zIndex: 1550 }}
        >
          <div
            className="toast show"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <div className="toast-header">
              <strong className="me-auto">Success</strong>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="toast"
                aria-label="Close"
                onClick={() => setShowToast(false)}
              ></button>
            </div>
            <div className="toast-body text-success text-center">
              Created successfully!
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UpdateRounds;
